import { useState, useCallback, useMemo } from "react";
import {
  SelectedSupplier,
  PartQuantity,
  SupplierPart,
  calculateRemainingParts,
} from "../utils/partsCalculationUtils";

const mergeSelectedPartsByRoot = (
  existing: SupplierPart[],
  incoming: SupplierPart[],
): SupplierPart[] => {
  const byRoot = new Map<string, SupplierPart>();
  for (const part of existing) {
    byRoot.set(part.rootPartNumber, { ...part });
  }
  for (const part of incoming) {
    const prev = byRoot.get(part.rootPartNumber);
    if (!prev) {
      byRoot.set(part.rootPartNumber, { ...part });
      continue;
    }
    byRoot.set(part.rootPartNumber, {
      ...prev,
      ...part,
      familyQuantity: Math.max(
        Number(prev.familyQuantity) || 0,
        Number(part.familyQuantity) || 0,
      ),
    });
  }
  return Array.from(byRoot.values()).filter(
    (part) => (Number(part.familyQuantity) || 0) > 0,
  );
};

export interface UseSelectedSuppliersReturn {
  selectedSuppliersInBucket: SelectedSupplier[];
  remainingPartsToFulfill: PartQuantity[];
  isOrderFullyFulfilled: boolean;
  orderFulfillmentPercentage: number;
  totalRemainingQuantity: number;
  addSupplierToSelectionBucket: (
    supplier: Omit<SelectedSupplier, "selectedAt">
  ) => void;
  removeSupplierFromSelectionBucket: (supplierId: string) => void;
  updateSupplierInSelectionBucket: (
    supplierId: string,
    updates: Partial<SelectedSupplier>
  ) => void;
  reducePartQuantityFromSupplierInBucket: (
    supplierId: string,
    partIdentity: {
      rootPartNumber?: string;
      partNumber?: string;
      partId?: string;
    },
    quantity: number
  ) => void;
  clearAllSuppliersFromSelectionBucket: () => void;
  /**
   * Replace PC-locked Confirmed suppliers with the latest hydration result.
   * Non-locked (manually added) suppliers are kept. Empty list clears all locked.
   */
  replacePartConfirmationLockedSuppliers: (
    suppliers: Array<Omit<SelectedSupplier, "selectedAt"> & { selectedAt?: Date }>,
  ) => void;
  getSupplierByIdFromSelectionBucket: (
    supplierId: string
  ) => SelectedSupplier | undefined;
}

export const useSelectedSuppliers = (
  originalParts: PartQuantity[],
  /**
   * Suppliers committed elsewhere (e.g. the Part Confirmation bucket) whose parts should
   * also be treated as exhausted when computing discovery remaining parts.
   */
  additionalCommittedSuppliers: SelectedSupplier[] = [],
): UseSelectedSuppliersReturn => {
  const [selectedSuppliersInBucket, setSelectedSuppliersInBucket] = useState<
    SelectedSupplier[]
  >([]);

  const remainingCalculation = useMemo(() => {
    return calculateRemainingParts(originalParts, [
      ...selectedSuppliersInBucket,
      ...additionalCommittedSuppliers,
    ]);
  }, [originalParts, selectedSuppliersInBucket, additionalCommittedSuppliers]);

  const addSupplierToSelectionBucket = useCallback(
    (supplier: Omit<SelectedSupplier, "selectedAt">) => {
      setSelectedSuppliersInBucket((prev) => {
        const existingIndex = prev.findIndex(
          (s) => s.supplierId === supplier.supplierId,
        );

        if (existingIndex >= 0) {
          const existing = prev[existingIndex];
          const updated = [...prev];
          // Merge selected parts when upserting a locked confirmed supplier.
          const mergedParts =
            supplier.isPartConfirmationLocked || existing.isPartConfirmationLocked
              ? mergeSelectedPartsByRoot(
                  existing.selectedParts,
                  supplier.selectedParts,
                )
              : supplier.selectedParts;
          updated[existingIndex] = {
            ...existing,
            ...supplier,
            selectedParts: mergedParts,
            isPartConfirmationLocked:
              Boolean(existing.isPartConfirmationLocked) ||
              Boolean(supplier.isPartConfirmationLocked),
            selectedAt: existing.selectedAt,
          };
          return updated;
        }
        return [...prev, { ...supplier, selectedAt: new Date() }];
      });
    },
    [],
  );

  const removeSupplierFromSelectionBucket = useCallback(
    (supplierId: string) => {
      setSelectedSuppliersInBucket((prev) =>
        prev.filter(
          (s) =>
            s.supplierId !== supplierId || Boolean(s.isPartConfirmationLocked),
        ),
      );
    },
    [],
  );

  const updateSupplierInSelectionBucket = useCallback(
    (supplierId: string, updates: Partial<SelectedSupplier>) => {
      setSelectedSuppliersInBucket((prev) =>
        prev.map((supplier) =>
          supplier.supplierId === supplierId
            ? { ...supplier, ...updates }
            : supplier
        )
      );
    },
    []
  );

  /** Reduces selected part qty on a supplier (unassign from estimate → Remaining Parts). */
  const reducePartQuantityFromSupplierInBucket = useCallback(
    (
      supplierId: string,
      partIdentity: {
        rootPartNumber?: string;
        partNumber?: string;
        partId?: string;
      },
      quantity: number,
    ) => {
      if (!supplierId || quantity <= 0) return;
      const matchKeys = new Set(
        [partIdentity.rootPartNumber, partIdentity.partNumber]
          .map((value) => String(value ?? "").trim())
          .filter(Boolean),
      );
      if (matchKeys.size === 0) return;

      setSelectedSuppliersInBucket((prev) => {
        const next: SelectedSupplier[] = [];
        for (const supplier of prev) {
          if (String(supplier.supplierId) !== String(supplierId)) {
            next.push(supplier);
            continue;
          }
          let remainingToReduce = quantity;
          const nextSelectedParts = supplier.selectedParts
            .map((part) => {
              if (
                remainingToReduce <= 0 ||
                !matchKeys.has(String(part.rootPartNumber))
              ) {
                return part;
              }
              const deducted = Math.min(part.familyQuantity, remainingToReduce);
              remainingToReduce -= deducted;
              return {
                ...part,
                familyQuantity: Math.max(0, part.familyQuantity - deducted),
              };
            })
            .filter((part) => part.familyQuantity > 0);
          if (nextSelectedParts.length > 0) {
            next.push({ ...supplier, selectedParts: nextSelectedParts });
          }
        }
        return next;
      });
    },
    []
  );

  const clearAllSuppliersFromSelectionBucket = useCallback(() => {
    setSelectedSuppliersInBucket([]);
  }, []);

  const replacePartConfirmationLockedSuppliers = useCallback(
    (
      suppliers: Array<
        Omit<SelectedSupplier, "selectedAt"> & { selectedAt?: Date }
      >,
    ) => {
      setSelectedSuppliersInBucket((prev) => {
        const nonLocked = prev.filter((s) => !s.isPartConfirmationLocked);
        const nextLocked = suppliers
          .filter((s) => (s.selectedParts?.length ?? 0) > 0)
          .map((supplier) => ({
            ...supplier,
            isPartConfirmationLocked: true,
            selectedAt: supplier.selectedAt ?? new Date(),
          }));
        return [...nonLocked, ...nextLocked];
      });
    },
    [],
  );

  const getSupplierByIdFromSelectionBucket = useCallback(
    (supplierId: string) => {
      return selectedSuppliersInBucket.find((s) => s.supplierId === supplierId);
    },
    [selectedSuppliersInBucket]
  );

  return {
    selectedSuppliersInBucket,
    remainingPartsToFulfill: remainingCalculation.remainingParts,
    isOrderFullyFulfilled: remainingCalculation.isFullyFulfilled,
    orderFulfillmentPercentage: remainingCalculation.fulfillmentPercentage,
    totalRemainingQuantity: remainingCalculation.totalRemainingQuantity,
    addSupplierToSelectionBucket,
    removeSupplierFromSelectionBucket,
    updateSupplierInSelectionBucket,
    reducePartQuantityFromSupplierInBucket,
    clearAllSuppliersFromSelectionBucket,
    replacePartConfirmationLockedSuppliers,
    getSupplierByIdFromSelectionBucket,
  };
};
